import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '~/hooks/useAppSelector';
import { apiMethodsConst } from '~/services/methods';
import { toCurrentIdentity } from '../adapters/UsersMe';
import { updateMe } from '../clients/UpdateMe';
import type { IUpdateMeApiPayload } from '../types/UsersMe.api';
import type { ICurrentIdentity } from '../types/UsersMe';
import { currentIdentityKeys } from './useGetMe';

/**
 * Saves the person's own name and shows it straight away from the response,
 * without a second read. The signed-in office record in redux carries the same
 * person's `first_name`/`last_name` (auth-service syncs it to the monolith
 * asynchronously), so it is patched too when its id is the person's — legacy
 * screens that greet the user by name then agree with My profile.
 *
 * Errors are not swallowed and skip the global toaster: the dialog shows them
 * and stays open.
 */
export const useUpdateMe = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const signedInOffice = useAppSelector((state) => state.user?.user ?? null);

  return useMutation<ICurrentIdentity, unknown, IUpdateMeApiPayload>({
    mutationFn: async (payload) => toCurrentIdentity(await updateMe(payload)),
    onSuccess: (identity) => {
      queryClient.setQueryData(currentIdentityKeys.me(), identity);

      if (signedInOffice?.id && signedInOffice.id === identity.id) {
        dispatch({
          type: apiMethodsConst.EDIT_USER_REQUEST,
          payload: {
            ...signedInOffice,
            first_name: identity.firstName ?? signedInOffice.first_name,
            last_name: identity.lastName ?? signedInOffice.last_name,
          },
        });
      }
    },
    meta: { skipGlobalErrorToaster: true },
  });
};
